/**
 * useNavigation — component-facing navigation API mirroring React Navigation's
 * useNavigation(). Screens call this instead of touching history directly.
 */

import { useEffect } from "react";
import { Platform } from "react-native";
import { useNavigationStore } from "./navigationStore";
import type { RouteName, RouteParams, Route } from "./routes";

export interface Navigation {
  route: Route;
  navigate: <N extends RouteName>(name: N, params?: RouteParams[N]) => void;
  replace: <N extends RouteName>(name: N, params?: RouteParams[N]) => void;
  goBack: () => void;
  canGoBack: () => boolean;
}

export function useNavigation(): Navigation {
  const route = useNavigationStore((s) => s.route);
  const navigate = useNavigationStore((s) => s.navigate);
  const replace = useNavigationStore((s) => s.replace);
  const goBack = useNavigationStore((s) => s.goBack);
  const canGoBack = useNavigationStore((s) => s.canGoBack);
  return { route, navigate, replace, goBack, canGoBack };
}

/** Installs web back/forward → route sync. Call once at the app root; no-op on native. */
export function useBrowserHistorySync(): void {
  const syncFromPath = useNavigationStore((s) => s.syncFromPath);
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const onPopState = () => {
      syncFromPath(window.location.pathname + window.location.search);
    };
    onPopState(); // reconcile once on mount
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [syncFromPath]);
}
